import { useCallback } from "react";

export interface UseFormHandlersReturn {
  handleInputChange: (name: string, value: unknown) => void;
  handleInputEvent: (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
}

/**
 * Form handlers hook for managing form state.
 * Accepts both nullable (T | null) and non-nullable (T) state setters.
 */
function useFormHandlers<T extends object>(
  setItem: React.Dispatch<React.SetStateAction<T | null>>
): UseFormHandlersReturn;
function useFormHandlers<T extends object>(setItem: React.Dispatch<React.SetStateAction<T>>): UseFormHandlersReturn;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function useFormHandlers(setItem: React.Dispatch<React.SetStateAction<any>>): UseFormHandlersReturn {
  /**
   * Handle input change directly with name and value
   */
  const handleInputChange = useCallback(
    (name: string, value: unknown) => {
      if (!name) return;

      setItem((prev: Record<string, unknown> | null) => {
        // Initialize with empty object if prev is null (for new item creation)
        const base = prev ?? {};

        // Support dot notation for nested fields (e.g., "userConfig.leverage")
        if (name.includes(".")) {
          const keys = name.split(".");
          const result = { ...base };
          let current: Record<string, unknown> = result;

          for (let i = 0; i < keys.length - 1; i++) {
            const key = keys[i];
            const existingValue = current[key];
            if (existingValue && typeof existingValue === "object" && !Array.isArray(existingValue)) {
              const obj = Object.assign<Record<string, unknown>, object>({}, existingValue);
              current[key] = obj;
              current = obj;
            } else {
              const obj: Record<string, unknown> = {};
              current[key] = obj;
              current = obj;
            }
          }

          current[keys[keys.length - 1]] = value;
          return result;
        }

        return { ...base, [name]: value };
      });
    },
    [setItem]
  );

  /**
   * Handle input event from form elements
   */
  const handleInputEvent = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
      const target = event?.target;
      if (!target) return;

      const name = target.name;
      if (!name) return;

      let finalValue: unknown;
      if (target.type === "checkbox" && "checked" in target) {
        finalValue = target.checked;
      } else {
        finalValue = target.value;
      }

      handleInputChange(name, finalValue);
    },
    [handleInputChange]
  );

  return {
    handleInputChange,
    handleInputEvent,
  };
}

export default useFormHandlers;
